import React, { useState } from 'react';
import { Box, Flex, Heading, Text, Button } from 'rebass';

const PlainOldTexts = () => {
  const [inputText, setInputText] = useState('');
  const [plainText, setPlainText] = useState('');
  const [showCopySuccess, setShowCopySuccess] = useState(false);

  // Function to strip formatting and convert to plain text
  const convertToPlainText = (text) => {
    // Create a temporary DOM element to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = text;
    
    let raw;
    if (tempDiv.innerText) {
      // Prefer innerText to preserve visual line breaks from block elements and <br>
      raw = tempDiv.innerText;
    } else {
      // Fallback for environments where innerText is not available (like Node.js tests)
      // Manually add line breaks for block elements and <br> tags
      const blockElements = tempDiv.querySelectorAll('p, div, h1, h2, h3, h4, h5, h6, li, blockquote, pre');
      blockElements.forEach(el => {
        el.insertAdjacentHTML('afterend', '\n');
      });
      
      // Handle <br> tags
      const brElements = tempDiv.querySelectorAll('br');
      brElements.forEach(br => {
        br.insertAdjacentHTML('afterend', '\n');
      });
      
      raw = tempDiv.textContent || '';
    }
    
    // Cleanup: normalize whitespace while preserving line breaks
    return raw
      .replace(/\r\n/g, '\n')           // Normalize CRLF to LF
      .replace(/[ \t\f\v]+/g, ' ')      // Collapse runs of spaces/tabs (not newlines)
      .replace(/[ \t]+\n/g, '\n')       // Trim trailing spaces before newline
      .replace(/\n[ \t]+/g, '\n')       // Trim leading spaces after newline
      .replace(/\n{3,}/g, '\n\n')       // Limit excessive blank lines
      .trim();
  };

  // Handle text input and automatically convert to plain text
  const handleTextChange = (event) => {
    const newText = event.target.value;
    setInputText(newText);
    setPlainText(convertToPlainText(newText));
  };

  // Handle copy to clipboard
  const handleCopyToClipboard = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        // Use the modern Clipboard API
        await navigator.clipboard.writeText(plainText);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = plainText;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
      }
      setShowCopySuccess(true);
    } catch (err) {
      console.error('Failed to copy text: ', err);
      // You could add error handling here if needed
    }
  };

  return (
    <Box maxWidth="800px" mx="auto" mt={4} mb={4} p={4} sx={{ borderRadius: 3, bg: 'muted' }}>
      <Heading as="h1" fontSize={4} mb={3} sx={{ fontFamily: "Menlo, 'IBM Plex Mono', ui-monospace, SFMono-Regular, Consolas, 'Liberation Mono', 'Courier New', monospace" }}>
        Plain Text Editor
      </Heading>
      <Text fontSize={2} color="gray" mb={3}>
        Paste formatted text (HTML, Markdown, or any styled content) below and it will be automatically converted to plain text.
      </Text>

      <Box mb={3}>
        <Text as="label" fontWeight="bold" mb={2} display="block">
          Input (Paste your formatted text here):
        </Text>
        <Box
          as="textarea"
          width="100%"
          rows={6}
          value={inputText}
          onChange={handleTextChange}
          sx={{
            fontFamily: 'inherit',
            fontSize: 2,
            p: 2,
            borderRadius: 2,
            border: '1px solid #ccc',
            mb: 2,
            resize: 'vertical'
          }}
          placeholder="Paste your formatted text, HTML, or Markdown here..."
        />
      </Box>

      <Box mb={3}>
        <Flex alignItems="center" mb={2}>
          <Text fontWeight="bold" mr={2}>
            Plain Text Output:
          </Text>
          <Button
            variant="outline"
            onClick={handleCopyToClipboard}
            disabled={!plainText.trim()}
            sx={{
              fontSize: 2,
              fontWeight: 500,
              px: 3,
              py: 2,
              ml: 2,
              borderRadius: 3,
              borderColor: '#b0b0b0',
              color: '#222',
              bg: '#fafbfc',
              boxShadow: 'none',
              letterSpacing: '0.01em',
              borderWidth: '1.5px',
              borderStyle: 'solid',
              transition: 'background 0.15s, border-color 0.15s, color 0.15s',
              cursor: plainText.trim() ? 'pointer' : 'not-allowed',
              opacity: plainText.trim() ? 1 : 0.5,
              '&:hover': {
                bg: '#f0f0f0',
                borderColor: '#222',
                color: '#111',
              }
            }}
          >
            Copy to Clipboard
          </Button>
        </Flex>
        <Box
          as="textarea"
          width="100%"
          rows={6}
          value={plainText}
          readOnly
          sx={{
            fontFamily: 'inherit',
            fontSize: 2,
            p: 2,
            borderRadius: 2,
            border: '1px solid #eee',
            bg: '#f5f5f5',
            resize: 'vertical'
          }}
          placeholder="Plain text will appear here..."
        />
      </Box>

      {plainText && (
        <Box mt={2} p={2} sx={{ bg: '#f0f0f0', borderRadius: 2 }}>
          <Text fontSize={1} color="gray">
            Character count: {plainText.length} | Words: {plainText.trim() ? plainText.trim().split(/\s+/).length : 0}
          </Text>
        </Box>
      )}

      {/* Snackbar/Alert implementation would need to be replaced with a custom solution or a simple message if not using MUI */}
      {showCopySuccess && (
        <Box
          mt={3}
          p={2}
          sx={{
            bg: 'green',
            color: 'white',
            borderRadius: 2,
            textAlign: 'center'
          }}
        >
          Plain text copied to clipboard!
        </Box>
      )}
    </Box>
  );
};

export default PlainOldTexts;
